import { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import { Task, TaskStatus } from "src/app/models/screens/task.model";
import {
  addTaskCollaborator,
  addUserTag,
  assignTaskToUser,
  createTask,
  deleteTask,
  getParentChildren,
  getTasksForDate,
  getUserTags,
  removeTaskCollaborator,
  requestMoreTime,
  searchUsers,
  toggleTaskPriority,
  updateTaskStatus,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

const TasksScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);

  const [tags, setTags] = useState<Map<string, string>>(new Map());
  const [selectedTagKey, setSelectedTagKey] = useState<string | undefined>(
    undefined,
  );
  const [newTagText, setNewTagText] = useState<string>("");
  const [newTagColor, setNewTagColor] = useState<string>("#1976d2");

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] =
    useState<boolean>(false);
  const [newTaskTitle, setNewTaskTitle] = useState<string>("");
  const [newTaskDescription, setNewTaskDescription] = useState<string>("");
  const [newTaskTag, setNewTaskTag] = useState<string>("");
  const [newTaskFromHour, setNewTaskFromHour] = useState<string>("");
  const [newTaskFromMinute, setNewTaskFromMinute] = useState<string>("");
  const [newTaskToHour, setNewTaskToHour] = useState<string>("");
  const [newTaskToMinute, setNewTaskToMinute] = useState<string>("");
  const [isCreatingTask, setIsCreatingTask] = useState<boolean>(false);
  const [newTaskIsPriority, setNewTaskIsPriority] = useState<boolean>(false);

  const [adminRequestUsers, setAdminRequestUsers] = useState<
    RequestManagementUser[]
  >([]);
  const [isLoadingAdminUsers, setIsLoadingAdminUsers] =
    useState<boolean>(false);

  const [isAssignTaskModalOpen, setIsAssignTaskModalOpen] =
    useState<boolean>(false);
  const [taskToAssign, setTaskToAssign] = useState<Task | null>(null);
  const [assignUserSearchText, setAssignUserSearchText] = useState<string>("");
  const [assignSearchedUsers, setAssignSearchedUsers] = useState<
    RequestManagementUser[]
  >([]);
  const [isSearchingAssignUsers, setIsSearchingAssignUsers] =
    useState<boolean>(false);
  const [selectedAssignUser, setSelectedAssignUser] =
    useState<RequestManagementUser | null>(null);
  const [isAssigningTask, setIsAssigningTask] = useState<boolean>(false);

  const [isMoreTimeModalOpen, setIsMoreTimeModalOpen] =
    useState<boolean>(false);
  const [taskForMoreTime, setTaskForMoreTime] = useState<Task | null>(null);
  const [moreTimeNewDate, setMoreTimeNewDate] = useState<string>("");
  const [moreTimeNewFromHour, setMoreTimeNewFromHour] = useState<string>("");
  const [moreTimeNewFromMinute, setMoreTimeNewFromMinute] =
    useState<string>("");
  const [moreTimeNewToHour, setMoreTimeNewToHour] = useState<string>("");
  const [moreTimeNewToMinute, setMoreTimeNewToMinute] = useState<string>("");
  const [isRequestingMoreTime, setIsRequestingMoreTime] =
    useState<boolean>(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState<boolean>(false);

  const [isCollaboratorModalOpen, setIsCollaboratorModalOpen] =
    useState<boolean>(false);
  const [taskForCollaborator, setTaskForCollaborator] = useState<Task | null>(
    null,
  );
  const [collaboratorSearchText, setCollaboratorSearchText] =
    useState<string>("");
  const [collaboratorSearchedUsers, setCollaboratorSearchedUsers] = useState<
    RequestManagementUser[]
  >([]);
  const [isSearchingCollaborators, setIsSearchingCollaborators] =
    useState<boolean>(false);
  const [selectedCollaboratorUser, setSelectedCollaboratorUser] =
    useState<RequestManagementUser | null>(null);
  const [isAddingCollaborator, setIsAddingCollaborator] =
    useState<boolean>(false);

  const isViewingAsAdmin = userRole === "Admin" && selectedOwnerId !== "you";

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const getCurrentAdminUserId = useCallback((): string | undefined => {
    if (userRole !== "Admin") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const normalizeTagKey = (s: string) => s.trim();

  const getUserTagsAsync = useCallback(async (childId?: string) => {
    const getTagsResponse = await getUserTags(childId);
    if (!getTagsResponse.success) {
      showError(getTagsResponse.error);
      return;
    }
    const nextTags = new Map<string, string>();
    Object.keys(getTagsResponse.tags ?? {}).forEach((key: string) => {
      nextTags.set(key, getTagsResponse.tags[key]);
    });
    setTags(nextTags);
  }, []);
  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadTasksForDate = useCallback(
    async (date: string) => {
      setIsLoadingTasks(true);
      const response = await getTasksForDate(
        date,
        getCurrentChildId(),
        getCurrentAdminUserId(),
      );
      setIsLoadingTasks(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      const sorted = [...(response.tasks ?? [])].sort(
        (a: Task, b: Task) => a.from - b.from,
      );
      setTasks(sorted);
    },
    [getCurrentAdminUserId, getCurrentChildId],
  );

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingAdminUsers(true);
    const response = await searchUsers("");
    setIsLoadingAdminUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAdminRequestUsers(response.users ?? []);
  }, []);

  const getStatusChipColor = (
    status: TaskStatus,
  ): "warning" | "success" | "error" | "default" => {
    if (status === "Completed") return "success";
    if (status === "CompletedOverdue") return "warning";
    if (status === "Incomplete") return "error";
    return "default";
  };

  useEffect(() => {
    const savedRole = localStorage.getItem(localStorageConstants.ROLE);
    if (!savedRole) return;
    setUserRole(savedRole);
  }, []);

  useEffect(() => {
    if (userRole === "Parent") {
      loadParentChildren();
    } else if (userRole === "Admin") {
      loadAdminUsers();
    }
  }, [loadAdminUsers, loadParentChildren, userRole]);

  useEffect(() => {
    const childId = getCurrentChildId();
    getUserTagsAsync(childId);
  }, [getCurrentChildId, getUserTagsAsync, selectedOwnerId]);

  useEffect(() => {
    if (!selectedDate || !userRole) return;
    loadTasksForDate(selectedDate.format("YYYY-MM-DD"));
  }, [loadTasksForDate, selectedDate, selectedOwnerId, userRole]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!selectedDate) return;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      if (selectedDate.format("YYYY-MM-DD") !== todayStr) return;
      const nowMinutes = today.getHours() * 60 + today.getMinutes();
      setTasks((prev) =>
        prev.map((task) => {
          if (task.status === "Ongoing" && task.to <= nowMinutes) {
            return { ...task, status: "Incomplete" };
          }
          return task;
        }),
      );
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const onDateChanged = (date: Dayjs | null) => {
    setSelectedDate(date);
    setTasks([]);
  };

  const onOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setTasks([]);
  };

  const openCreateTaskModal = () => {
    setNewTaskTitle("");
    setNewTaskDescription("");
    setNewTaskTag("");
    setSelectedTagKey(undefined);
    setNewTagText("");
    setNewTagColor("#1976d2");
    setNewTaskFromHour("");
    setNewTaskFromMinute("");
    setNewTaskToHour("");
    setNewTaskToMinute("");
    setNewTaskIsPriority(false);
    setIsCreateTaskModalOpen(true);
  };

  const closeCreateTaskModal = () => {
    setIsCreateTaskModalOpen(false);
  };

  const onCreateTaskConfirmed = async () => {
    if (!selectedDate) {
      showError("Please select a date first.");
      return;
    }
    if (!newTaskTitle.trim()) {
      showError("Please provide a title.");
      return;
    }
    if (!newTaskDescription.trim()) {
      showError("Please provide a description.");
      return;
    }
    if (!newTaskTag.trim() && !selectedTagKey) {
      showError("Please select or create a tag.");
      return;
    }
    const tagValue = selectedTagKey ?? newTaskTag.trim();
    const fromH = parseInt(newTaskFromHour);
    const fromM = parseInt(newTaskFromMinute);
    const toH = parseInt(newTaskToHour);
    const toM = parseInt(newTaskToMinute);
    if (
      isNaN(fromH) ||
      isNaN(fromM) ||
      isNaN(toH) ||
      isNaN(toM) ||
      fromH < 0 ||
      fromH > 23 ||
      fromM < 0 ||
      fromM > 59 ||
      toH < 0 ||
      toH > 23 ||
      toM < 0 ||
      toM > 59
    ) {
      showError("Please enter valid start and end times.");
      return;
    }
    const from = fromH * 60 + fromM;
    const to = toH * 60 + toM;
    if (to <= from) {
      showError("End time must be after start time.");
      return;
    }
    setIsCreatingTask(true);
    const response = await createTask(
      selectedDate.format("YYYY-MM-DD"),
      newTaskTitle.trim(),
      newTaskDescription.trim(),
      tagValue,
      from,
      to,
      getCurrentChildId(),
      newTaskIsPriority,
    );
    setIsCreatingTask(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess("Task created successfully.");
    closeCreateTaskModal();
    await loadTasksForDate(selectedDate.format("YYYY-MM-DD"));
  };

  const onMarkTaskStatus = async (taskId: string, status: TaskStatus) => {
    if (isViewingAsAdmin) return;
    const response = await updateTaskStatus(
      taskId,
      status,
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status } : t)),
    );
  };

  const onToggleTaskPriority = async (taskId: string) => {
    if (isViewingAsAdmin) return;
    const response = await toggleTaskPriority(taskId, getCurrentChildId());
    if (!response.success) {
      showError(response.error);
      return;
    }
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId ? { ...t, isPriority: response.task.isPriority } : t,
      ),
    );
  };

  const openDeleteModal = (task: Task) => {
    setTaskToDelete(task);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  const onDeleteTaskConfirmed = async () => {
    if (!taskToDelete) return;
    setIsDeletingTask(true);
    const response = await deleteTask(taskToDelete.id, getCurrentChildId());
    setIsDeletingTask(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess("Task deleted.");
    closeDeleteModal();
    setTasks((prev) => prev.filter((t) => t.id !== taskToDelete.id));
  };

  const openAssignTaskModal = (task: Task) => {
    setTaskToAssign(task);
    setAssignUserSearchText("");
    setAssignSearchedUsers([]);
    setSelectedAssignUser(null);
    setIsAssignTaskModalOpen(true);
  };

  const closeAssignTaskModal = () => {
    setIsAssignTaskModalOpen(false);
    setTaskToAssign(null);
    setSelectedAssignUser(null);
  };

  const loadCollaboratorUsers = useCallback(
    async (query: string) => {
      setIsSearchingCollaborators(true);
      const response = await searchUsers(query, getCurrentChildId());
      setIsSearchingCollaborators(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setCollaboratorSearchedUsers(response.users ?? []);
    },
    [getCurrentChildId],
  );

  const loadAssignUsers = useCallback(
    async (query: string) => {
      setIsSearchingAssignUsers(true);
      const response = await searchUsers(query, getCurrentChildId());
      setIsSearchingAssignUsers(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setAssignSearchedUsers(response.users ?? []);
    },
    [getCurrentChildId],
  );

  useEffect(() => {
    if (!isAssignTaskModalOpen) return;
    const timeout = setTimeout(() => {
      loadAssignUsers(assignUserSearchText.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [assignUserSearchText, isAssignTaskModalOpen, loadAssignUsers]);

  const onAssignTaskConfirmed = async () => {
    if (!taskToAssign || !selectedAssignUser) return;
    const isDirectAssign =
      userRole === "Parent" &&
      children.some((c) => c._id === selectedAssignUser.id);
    setIsAssigningTask(true);
    const response = await assignTaskToUser(
      taskToAssign.id,
      selectedAssignUser.id,
      isDirectAssign,
      getCurrentChildId(),
    );
    setIsAssigningTask(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess(
      isDirectAssign
        ? "Task directly assigned to child."
        : "Task assignment request sent.",
    );
    closeAssignTaskModal();
    await loadTasksForDate(selectedDate!.format("YYYY-MM-DD"));
  };

  const openMoreTimeModal = (task: Task) => {
    setTaskForMoreTime(task);
    setMoreTimeNewDate(task.date);
    const currentFromH = Math.floor(task.from / 60);
    const currentFromM = task.from % 60;
    const currentToH = Math.floor(task.to / 60);
    const currentToM = task.to % 60;
    setMoreTimeNewFromHour(currentFromH.toString());
    setMoreTimeNewFromMinute(currentFromM.toString().padStart(2, "0"));
    setMoreTimeNewToHour(currentToH.toString());
    setMoreTimeNewToMinute(currentToM.toString().padStart(2, "0"));
    setIsMoreTimeModalOpen(true);
  };

  const closeMoreTimeModal = () => {
    setIsMoreTimeModalOpen(false);
    setTaskForMoreTime(null);
    setMoreTimeNewDate("");
    setMoreTimeNewFromHour("");
    setMoreTimeNewFromMinute("");
    setMoreTimeNewToHour("");
    setMoreTimeNewToMinute("");
  };

  const onMoreTimeConfirmed = async () => {
    if (!taskForMoreTime) return;
    if (!moreTimeNewDate.trim()) {
      showError("Please provide a new date.");
      return;
    }
    const fromH = parseInt(moreTimeNewFromHour);
    const fromM = parseInt(moreTimeNewFromMinute);
    const toH = parseInt(moreTimeNewToHour);
    const toM = parseInt(moreTimeNewToMinute);
    if (
      isNaN(fromH) ||
      isNaN(fromM) ||
      isNaN(toH) ||
      isNaN(toM) ||
      fromH < 0 ||
      fromH > 23 ||
      fromM < 0 ||
      fromM > 59 ||
      toH < 0 ||
      toH > 23 ||
      toM < 0 ||
      toM > 59
    ) {
      showError("Please enter valid start and end times.");
      return;
    }
    const newFrom = fromH * 60 + fromM;
    const newTo = toH * 60 + toM;
    if (newTo <= newFrom) {
      showError("End time must be after start time.");
      return;
    }
    setIsRequestingMoreTime(true);
    const response = await requestMoreTime(
      taskForMoreTime.id,
      moreTimeNewDate.trim(),
      newFrom,
      newTo,
      getCurrentChildId(),
    );
    setIsRequestingMoreTime(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess("Reschedule request sent.");
    closeMoreTimeModal();
  };

  const openCollaboratorModal = (task: Task) => {
    setTaskForCollaborator(task);
    setCollaboratorSearchText("");
    setCollaboratorSearchedUsers([]);
    setSelectedCollaboratorUser(null);
    setIsCollaboratorModalOpen(true);
  };

  const closeCollaboratorModal = () => {
    setIsCollaboratorModalOpen(false);
    setTaskForCollaborator(null);
    setSelectedCollaboratorUser(null);
  };

  useEffect(() => {
    if (!isCollaboratorModalOpen) return;
    const timeout = setTimeout(() => {
      loadCollaboratorUsers(collaboratorSearchText.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [collaboratorSearchText, isCollaboratorModalOpen, loadCollaboratorUsers]);

  const onAddCollaboratorConfirmed = async () => {
    if (!taskForCollaborator || !selectedCollaboratorUser) return;
    setIsAddingCollaborator(true);
    const response = await addTaskCollaborator(
      taskForCollaborator.id,
      selectedCollaboratorUser.id,
      getCurrentChildId(),
    );
    setIsAddingCollaborator(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess("Collaborator added.");
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskForCollaborator.id
          ? {
              ...t,
              collaborators: response.task.collaborators,
            }
          : t,
      ),
    );
    closeCollaboratorModal();
  };

  const onRemoveCollaborator = async (
    taskId: string,
    collaboratorUserId: string,
  ) => {
    if (isViewingAsAdmin) return;
    const response = await removeTaskCollaborator(
      taskId,
      collaboratorUserId,
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess("Collaborator removed.");
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? { ...t, collaborators: response.task.collaborators }
          : t,
      ),
    );
  };

  const toggleTag = (tagKey: string) => {
    setSelectedTagKey(tagKey);
  };

  const onCreateTag = async () => {
    const normalized = normalizeTagKey(newTagText);
    if (!normalized) return;
    const exists = Array.from(tags.keys()).some(
      (t: string) =>
        normalizeTagKey(t).toLowerCase() === normalized.toLowerCase(),
    );
    if (exists) {
      showError("Tag already exists");
      return;
    }
    const childId = getCurrentChildId();
    const addTagResponse = await addUserTag(newTagText, newTagColor, childId);
    if (!addTagResponse.success) {
      showError(addTagResponse.error);
      return;
    }
    const nextTags = new Map<string, string>();
    Object.keys(addTagResponse.tags ?? {}).forEach((key: string) => {
      nextTags.set(key, addTagResponse.tags[key]);
    });
    setTags(nextTags);
    setNewTagText("");
  };

  const canManageTasks = useMemo(() => {
    return !isViewingAsAdmin;
  }, [isViewingAsAdmin]);

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    selectedDate,
    onDateChanged,
    tasks,
    isLoadingTasks,
    minutesToLabel,
    getStatusChipColor,
    canManageTasks,
    isViewingAsAdmin,
    isCreateTaskModalOpen,
    openCreateTaskModal,
    closeCreateTaskModal,
    newTaskTitle,
    setNewTaskTitle,
    newTaskDescription,
    setNewTaskDescription,
    newTaskTag,
    setNewTaskTag,
    newTaskFromHour,
    setNewTaskFromHour,
    newTaskFromMinute,
    setNewTaskFromMinute,
    newTaskToHour,
    setNewTaskToHour,
    newTaskToMinute,
    setNewTaskToMinute,
    isCreatingTask,
    onCreateTaskConfirmed,
    newTaskIsPriority,
    setNewTaskIsPriority,
    tags,
    selectedTagKey,
    toggleTag,
    newTagText,
    setNewTagText,
    newTagColor,
    setNewTagColor,
    onCreateTag,
    normalizeTagKey,
    onMarkTaskStatus,
    onToggleTaskPriority,
    isDeleteModalOpen,
    taskToDelete,
    openDeleteModal,
    closeDeleteModal,
    isDeletingTask,
    onDeleteTaskConfirmed,
    isAssignTaskModalOpen,
    taskToAssign,
    openAssignTaskModal,
    closeAssignTaskModal,
    assignUserSearchText,
    setAssignUserSearchText,
    assignSearchedUsers,
    isSearchingAssignUsers,
    selectedAssignUser,
    setSelectedAssignUser,
    isAssigningTask,
    onAssignTaskConfirmed,
    isMoreTimeModalOpen,
    taskForMoreTime,
    openMoreTimeModal,
    closeMoreTimeModal,
    moreTimeNewDate,
    setMoreTimeNewDate,
    moreTimeNewFromHour,
    setMoreTimeNewFromHour,
    moreTimeNewFromMinute,
    setMoreTimeNewFromMinute,
    moreTimeNewToHour,
    setMoreTimeNewToHour,
    moreTimeNewToMinute,
    setMoreTimeNewToMinute,
    isRequestingMoreTime,
    onMoreTimeConfirmed,
    isCollaboratorModalOpen,
    taskForCollaborator,
    openCollaboratorModal,
    closeCollaboratorModal,
    collaboratorSearchText,
    setCollaboratorSearchText,
    collaboratorSearchedUsers,
    isSearchingCollaborators,
    selectedCollaboratorUser,
    setSelectedCollaboratorUser,
    isAddingCollaborator,
    onAddCollaboratorConfirmed,
    onRemoveCollaborator,
    adminRequestUsers,
    isLoadingAdminUsers,
  };
};

export default TasksScreenViewModel;
